import { Component, Input, Output, EventEmitter, OnInit, ViewChild, ElementRef, forwardRef, TemplateRef } from '@angular/core';

import { NG_VALUE_ACCESSOR, NG_VALIDATORS, ControlValueAccessor, UntypedFormControl, Validator } from '@angular/forms';

import { of as observableOf, Observable, Subject } from 'rxjs';
import { catchError, switchMap, distinctUntilChanged, debounceTime, takeUntil } from 'rxjs/operators';

import { DestroyService } from '../../core/services';

interface SelectStates {
  showList: boolean;
  index: number;
}

interface SearchDetails {
  name: string;
  value: any;
  extra: any;
}

enum SelectMode {
  Static = 'static',
  Sync = 'sync',
  Async = 'async'
}

@Component({
  selector: 'bc-select',
  templateUrl: 'bc.select.component.html',
  styleUrls: ['bc.select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BcSelectComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => BcSelectComponent),
      multi: true,
    },
    DestroyService
  ]
})
export class BcSelectComponent implements OnInit, ControlValueAccessor, Validator {
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  @Input() list: Array<any>;
  @Input() searchAvailable: boolean;
  @Input() plugin: any;
  @Input() disable: boolean;
  @Input() placeholder: string;
  @Input() exactMatch = false;
  @Input() id: string;
  @Input() iteratedTemplate: TemplateRef<any>;
  @Input() isFilterable = true;
  @Output() isEmptyResult = new EventEmitter<boolean>();
  @Input() set resetPreviousValue(value) {
    this.previousValue = '';
  }

  @Input() mode: SelectMode = SelectMode.Static;

  @Output() asyncSelected = new EventEmitter();

  states: SelectStates;

  listAsync: Observable<Array<SearchDetails>>;

  filteredList: Array<{name: string, value: any}>;
  searchQuery = new Subject<string>();

  private onModelChange: any;

  private previousValue: string;
  private savedValue: string;
  private closing: any;

  private innerValue: any;
  private wrongName: boolean;

  constructor(private destroy$: DestroyService) {
    this.onModelChange = () => {  };
    this.states = {
      showList: false,
      index: -1
    };

    this.previousValue = '';
    this.savedValue = '';
  }

  ngOnInit() {
    if (!this.mode || this.mode === SelectMode.Static) {
      if (Array.isArray(this.list)) {
        this.filter();
        this.mode = SelectMode.Static;

        return;
      }
    }

    if (!this.iteratedTemplate) throw new Error('No template provided');
    if (!this.plugin) throw new Error('No plugin provided');
    if (!this.plugin.getList) throw new Error('Plugin is not realize getList method');
    if (!this.plugin.getList) throw new Error('Plugin is not realize next method');

    this.list = [];
    this.filter();

    if (this.mode === SelectMode.Async) {
      const debounceValue = 300;
      this.listAsync = this.searchQuery.pipe(
        debounceTime(debounceValue),
        distinctUntilChanged(),
        switchMap((query: string) => {
          if (query) return <Observable<Array<SearchDetails>>> this.plugin.getList(query);

          return observableOf<Array<SearchDetails>>([]);
        }),
        catchError(() => observableOf<Array<SearchDetails>>([])));

      this.listAsync.pipe(takeUntil(this.destroy$)).subscribe(
        (res: any) => {
          this.list = res;
          this.filter(this.searchInput.nativeElement.value);
          this.states.index = -1;
        },
        (err) => console.error(err)
      );
    }
  }

  writeValue(value: any): void {
    if (value !== undefined) {
      const result = this.find(value);
      this.searchInput.nativeElement.value = result.name;
    }
  }

  registerOnChange(fn: any): void {
    this.onModelChange = fn;
  }

  registerOnTouched() {  }

  setDisabledState(isDisabled: boolean): void {
    this.disable = isDisabled;
  }

  validate(c: UntypedFormControl) {
    return (!this.wrongName) ? null : {
      invalidCountryName: {name}
    };
  }

  keyUp(key: KeyboardEvent) {

    if (!this.searchAvailable) {
      return;
    }

    switch (key.code) {
      case 'ArrowUp':
        this.states.showList = true;
        if (this.states.index === -1) break;

        this.states.index--;
        this.searchInput.nativeElement.value = this.getName(this.states.index);
        this.onModelChange(this.getValue(this.states.index));
        break;

      case 'ArrowDown':
        this.states.showList = true;
        if (this.states.index === -1) this.saveValue(this.searchInput.nativeElement.value);
        if (this.states.index === this.list.length - 1) break;

        this.states.index++;
        this.searchInput.nativeElement.value = this.getName(this.states.index);

        this.onModelChange(this.getValue(this.states.index));
        break;

      case 'Escape':
        if (!this.searchInput.nativeElement.value) {
          this.states.showList = false;
        }

        this.searchInput.nativeElement.value = '';
        this.states.index = -1;

        this.onModelChange(this.getValue(this.states.index));
        break;

      case 'Enter':
        if (this.states.index !== -1) {
          this.states.showList = false;
          this.previousValue = this.getValue(this.states.index);
          this.onModelChange(this.getValue(this.states.index));
          this.emitSelectEvent();
        } else {
          this.search();
        }
        break;

      default:
        this.states.index = -1;

        if (this.mode === SelectMode.Async) {
          this.searchQuery.next(this.searchInput.nativeElement.value);
        }

        this.filter(this.searchInput.nativeElement.value);
        this.onModelChange(this.getValue(this.states.index));
    }

    if (!this.list[this.states.index]) this.onModelChange(this.searchInput.nativeElement.value);
  }

  checkTab(key: KeyboardEvent) {

    if (this.searchAvailable === false) {
      return;
    }

    switch (key.code) {
      case 'Tab':
        if (this.states.index !== -1) {
          this.states.showList = false;
          this.previousValue = this.getValue(this.states.index);
          this.onModelChange(this.getValue(this.states.index));
          this.emitSelectEvent();
        }
        break;
      default:
        break;
    }

    if (!this.list[this.states.index]) this.onModelChange(this.searchInput.nativeElement.value);
  }

  focus() {
    this.states.showList = true;
  }

  toogleFocus() {
    this.states.showList = !this.states.showList;
  }

  search() {
    if (this.mode === SelectMode.Static) {
      this.searchInput.nativeElement.value = this.find(this.searchInput.nativeElement.value).name;
    }

    if (this.mode === SelectMode.Sync) {
      this.searchInput.nativeElement.value = this.find(this.searchInput.nativeElement.value).name || this.searchInput.nativeElement.value;
      if (this.valueNotChanged()) {
        this.states.showList = true;

        return;
      }

      const searchValue = this.searchInput.nativeElement.value;
      if (searchValue) {
        this.previousValue = searchValue;
        this.plugin.getList(searchValue).subscribe((res: any) => {
          this.isEmptyResult.emit(res.length !== 0);
          this.list = res;
          this.filter(searchValue);
          this.states.showList = true;
          this.states.index = -1;
          this.searchInput.nativeElement.focus();
        }, (err) => console.error(err));
      }
    }

    if (this.mode === SelectMode.Async) {
      this.searchInput.nativeElement.value = this.find(this.searchInput.nativeElement.value).name || this.searchInput.nativeElement.value;
    }
  }

  blur() {
    const timeoutValue = 200;
    this.closing = setTimeout(() => { this.states.showList = false; }, timeoutValue);

    if (this.mode === SelectMode.Static) {
      const index = this.filteredList.findIndex((item) => item.name === this.searchInput.nativeElement.value);

      if (index !== -1) {
        this.searchInput.nativeElement.value = this.getName(index);
        this.onModelChange(this.getValue(index));

        return;
      }

      if (this.filteredList.length) {
        this.searchInput.nativeElement.value = this.getName(0);
        this.onModelChange(this.getValue(0));
        this.wrongName = false;
      } else {
        this.wrongName = true;
        this.onModelChange(this.searchInput.nativeElement.value);
      }
    }

  }

  select(item: string, index: number) {
    this.closing = null;
    this.searchInput.nativeElement.value = this.getName(index);
    this.onModelChange(this.getValue(index));
    this.states.index = index;
    this.searchInput.nativeElement.focus();
    this.states.showList = false;

    this.emitSelectEvent();
  }

  emitSelectEvent() {
    if (this.mode === SelectMode.Sync || this.mode === SelectMode.Async) {
      this.asyncSelected.emit(this.list[this.states.index] || {name: this.searchInput.nativeElement.value});
      if (!this.list[this.states.index]) this.onModelChange(this.searchInput.nativeElement.value);
    }
  }

  protected find(value?: any) {
    const filteredValue: any = this.list.find((item: any) => {
      if ((typeof value === 'string' || value instanceof String) && !this.exactMatch) {
        return item.value === value.toUpperCase() || item.name.toLowerCase() === value.toLowerCase();
      }

      return item.value === value;
    });

    return filteredValue || {name: '', value: ''};
  }

  protected filter(query?: string) {
    if (query && this.isFilterable) {
      this.filteredList = this.list.filter((item) => {
        const result: number = item.name.toLowerCase().search(query.toLowerCase());

        return result + 1;
      });

      return;
    }
    this.filteredList = this.list;
  }

  protected getName(index: number) {
    return (index === -1) ? this.savedValue : this.filteredList[index].name;
  }

  protected getValue(index: number) {
    if (index !== -1) {
      return this.filteredList[index].value;
    }

    return this.find(this.searchInput.nativeElement.value).value;
  }

  private saveValue(value: any) {
    this.savedValue = value;
  }

  private valueNotChanged() {
    return this.previousValue === this.searchInput.nativeElement.value;
  }

}
